import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import mqtt from 'mqtt';
import SchemaNode from './SchemaNode';

// Generic, blueprint-driven Studio page (plan §2.1). Renders entirely from the
// blueprint config fetched at /api/studio/{blueprint}/config — agents, intake
// params, and the display schema. No studio-specific code lives here.

const WS_URL = 'ws://localhost:9001';

const IMPORTANCE_COLORS = {
    critical: { bg: '#fef2f2', text: '#dc2626' },
    high: { bg: '#fffbeb', text: '#d97706' },
    medium: { bg: '#eff6ff', text: '#2563eb' },
    low: { bg: '#f0fdf4', text: '#16a34a' },
};

function Pill({ level }) {
    const c = IMPORTANCE_COLORS[String(level)] || IMPORTANCE_COLORS.medium;
    return <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.text }}>{String(level)}</span>;
}

function ScoreBadge({ score, size = 'md' }) {
    if (score == null) return null;
    const n = Number(score);
    const color = n >= 8 ? '#10b981' : n >= 6 ? '#f59e0b' : n >= 4 ? '#f97316' : '#ef4444';
    const dim = size === 'lg' ? 40 : 26;
    return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: dim, height: dim, borderRadius: '50%', background: color, color: '#fff', fontWeight: 700, fontSize: size === 'lg' ? 16 : 12 }}>{score}</span>;
}

function StatusPill({ status }) {
    const map = {
        idle: { bg: '#6b7280', label: 'Idle' },
        running: { bg: '#3b82f6', label: 'Running…' },
        waiting: { bg: '#f59e0b', label: 'Waiting for you' },
        producing_json: { bg: '#8b5cf6', label: 'Producing…' },
        accepted: { bg: '#10b981', label: 'Accepted' },
        stopped: { bg: '#ef4444', label: 'Stopped' },
    };
    const { bg, label } = map[status] || map.idle;
    return <span style={{ padding: '4px 12px', borderRadius: 999, background: bg, color: '#fff', fontSize: 13, fontWeight: 600 }}>{label}</span>;
}

// ── Agent card ──
function AgentCard({ meta, data, steer, onSteer, onRerun, running }) {
    const [open, setOpen] = useState(false);
    const accent = meta.color || '#6b7280';
    const issues = data?.issues || [];
    const suggestions = data?.suggestions || [];
    const questions = data?.follow_up_questions || [];
    const strengths = data?.strengths || [];
    return (
        <div style={{ border: `1px solid ${accent}33`, borderLeft: `4px solid ${accent}`, borderRadius: 8, marginBottom: 12, background: '#fff' }}>
            <div onClick={() => setOpen(!open)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: `${accent}08` }}>
                <span style={{ fontWeight: 700, color: accent, fontSize: 13 }}>{meta.label}</span>
                {data && <ScoreBadge score={data.score} />}
                {data?.confidence != null && <span style={{ fontSize: 11, color: '#9ca3af' }}>{Math.round(data.confidence * 100)}%</span>}
                <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11 }}>
                    {issues.length > 0 && `${issues.length} issues`}{questions.length > 0 && ` · ${questions.length} Q`}
                </span>
                <span style={{ color: '#9ca3af' }}>{open ? '▾' : '▸'}</span>
            </div>
            {data && !open && <div style={{ padding: '6px 14px 10px', fontSize: 13, color: '#4b5563' }}>{data.summary}</div>}
            {open && data && (
                <div style={{ padding: '10px 14px 14px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: '#374151' }}>{data.summary}</p>
                    {strengths.length > 0 && <Group title="Strengths" color="#059669">{strengths.map((s, i) => <div key={i} style={{ fontSize: 12, paddingLeft: 10, borderLeft: '2px solid #10b98144', marginBottom: 2 }}>{s}</div>)}</Group>}
                    {issues.length > 0 && <Group title="Issues" color="#dc2626">{issues.map((it) => (
                        <div key={it.id} style={{ marginBottom: 6, padding: '6px 8px', borderRadius: 6, background: '#fef2f2' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Pill level={it.severity} /><strong style={{ fontSize: 12 }}>{it.title}</strong></div>
                            <div style={{ fontSize: 12, color: '#4b5563' }}>{it.description}</div>
                            {it.suggested_fix && <div style={{ fontSize: 11, color: '#059669', marginTop: 3 }}>Fix: {it.suggested_fix}</div>}
                        </div>
                    ))}</Group>}
                    {suggestions.length > 0 && <Group title="Suggestions" color="#2563eb">{suggestions.map((s) => (
                        <div key={s.id} style={{ marginBottom: 4, padding: '4px 8px', borderRadius: 6, background: '#eff6ff' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Pill level={s.priority} /><strong style={{ fontSize: 12 }}>{s.title}</strong></div>
                            <div style={{ fontSize: 12, color: '#4b5563' }}>{s.description}</div>
                        </div>
                    ))}</Group>}
                    {questions.length > 0 && <Group title="Questions" color="#7c3aed">{questions.map((q, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#4b5563', padding: '2px 0' }}>{q.blocking ? '‼️ ' : '? '}{q.question}{q.blocking && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 10, marginLeft: 4 }}>BLOCKING</span>}</div>
                    ))}</Group>}
                    <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                        <input type="text" placeholder="Steering note…" value={steer || ''} onChange={(e) => onSteer(meta.key, e.target.value)} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }} />
                        <button onClick={() => onRerun(meta.key)} disabled={running} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: running ? '#d1d5db' : accent, color: '#fff', fontSize: 12, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer' }}>Re-run</button>
                    </div>
                </div>
            )}
            {!data && <div style={{ padding: '8px 14px', color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>Waiting for first round…</div>}
        </div>
    );
}

function Group({ title, color, children }) {
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</div>
            {children}
        </div>
    );
}

// ── Question inbox ──
function QuestionInbox({ agentOutputs, agentLabels }) {
    const all = useMemo(() => {
        const qs = [];
        Object.entries(agentOutputs).forEach(([k, d]) => (d.follow_up_questions || []).forEach((q, i) => qs.push({ ...q, agent: k, key: `${k}_${i}` })));
        qs.sort((a, b) => (b.blocking ? 1 : 0) - (a.blocking ? 1 : 0));
        return qs;
    }, [agentOutputs]);
    if (all.length === 0) return null;
    const blocking = all.filter((q) => q.blocking).length;
    return (
        <div style={{ border: '1px solid #7c3aed33', borderLeft: '4px solid #7c3aed', borderRadius: 8, marginBottom: 16, background: '#fff', padding: '10px 14px' }}>
            <div style={{ fontWeight: 700, color: '#7c3aed', fontSize: 14, marginBottom: 6 }}>Questions from Agents {blocking > 0 && <span style={{ color: '#dc2626' }}>({blocking} blocking)</span>}</div>
            {all.map((q) => (
                <div key={q.key} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
                    <span style={{ color: '#6b7280', fontSize: 11 }}>{agentLabels[q.agent] || q.agent}</span>
                    <span style={{ flex: 1 }}>{q.question}</span>
                    {q.blocking && <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 10 }}>BLOCKING</span>}
                </div>
            ))}
        </div>
    );
}

// ── Coordinator panel ──
function CoordinatorPanel({ data, agentLabels, selected, onToggle, onAll, onNone, onProduce, producing }) {
    if (!data) return null;
    const changes = data.proposed_changes || [];
    const selectedCount = Object.values(selected).filter(Boolean).length;
    // group changes by target
    const groups = {};
    changes.forEach((c) => { const t = c.target || 'general'; (groups[t] = groups[t] || []).push(c); });
    return (
        <div style={{ border: '1px solid #6366f144', borderLeft: '4px solid #6366f1', borderRadius: 8, background: '#fff', marginBottom: 16, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontWeight: 700, color: '#4338ca', fontSize: 14 }}>Moderator</span>
                <ScoreBadge score={data.overall} size="lg" />
                <span style={{ color: '#6b7280', fontSize: 12 }}>Round {data.round}</span>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                {data.scores && Object.entries(data.scores).map(([k, v]) => (
                    <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}><ScoreBadge score={v} /> {agentLabels[k] || k}</span>
                ))}
            </div>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 12px' }}>{data.summary}</p>

            {(data.agreements || []).length > 0 && (
                <Group title="Agents Agree" color="#059669">{data.agreements.map((a, i) => (
                    <div key={i} style={{ padding: '6px 10px', borderRadius: 6, marginBottom: 4, background: '#f0fdf4' }}>
                        <strong style={{ fontSize: 12 }}>{a.theme}</strong>
                        <div style={{ fontSize: 12, color: '#4b5563' }}>{a.detail}</div>
                    </div>
                ))}</Group>
            )}
            {(data.tensions || []).length > 0 && (
                <Group title="Tensions" color="#d97706">{data.tensions.map((t, i) => (
                    <div key={i} style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 4, background: '#fffbeb' }}>
                        <strong style={{ fontSize: 12 }}>{t.topic}</strong>
                        {(t.sides || []).map((s, j) => <div key={j} style={{ fontSize: 12, color: '#4b5563' }}>· {s.position}</div>)}
                        {t.coordinator_take && <div style={{ fontSize: 12, color: '#4338ca', marginTop: 4 }}>Take: {t.coordinator_take}</div>}
                    </div>
                ))}</Group>
            )}

            {changes.length > 0 && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong style={{ fontSize: 13 }}>Proposed Changes ({selectedCount}/{changes.length})</strong>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={onAll} style={btnSm}>All</button>
                            <button onClick={onNone} style={btnSm}>None</button>
                        </div>
                    </div>
                    {Object.entries(groups).map(([target, items]) => (
                        <div key={target} style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>{target}</div>
                            {items.map((c) => (
                                <div key={c.id} onClick={() => onToggle(c.id)} style={{ display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 6, border: `1px solid ${selected[c.id] ? '#93c5fd' : '#e5e7eb'}`, background: selected[c.id] ? '#eff6ff' : '#fafafa', marginBottom: 6, cursor: 'pointer', opacity: selected[c.id] ? 1 : 0.6 }}>
                                    <input type="checkbox" checked={!!selected[c.id]} readOnly />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}><strong style={{ fontSize: 12 }}>{c.title}</strong><Pill level={c.importance} />{c.layer && <span style={{ fontSize: 10, color: '#6b7280' }}>{c.layer}</span>}</div>
                                        <div style={{ fontSize: 12, color: '#4b5563' }}>{c.description}</div>
                                        {c.rationale && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}><em>Why:</em> {c.rationale}</div>}
                                        {c.downstream_effect && <div style={{ fontSize: 11, color: '#6b7280' }}><em>Downstream:</em> {c.downstream_effect}</div>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                    <button onClick={onProduce} disabled={selectedCount === 0 || producing} style={{ width: '100%', marginTop: 8, padding: '10px 0', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 700, color: '#fff', background: producing ? '#8b5cf6' : selectedCount === 0 ? '#d1d5db' : '#059669', cursor: selectedCount === 0 || producing ? 'not-allowed' : 'pointer' }}>
                        {producing ? 'Producing…' : `Produce with ${selectedCount} change${selectedCount !== 1 ? 's' : ''}`}
                    </button>
                </div>
            )}
        </div>
    );
}

const btnSm = { padding: '3px 8px', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff', fontSize: 11, cursor: 'pointer' };

// ── Intake (generic, param-driven) ──
function Intake({ inputs, params, setParam, disabled }) {
    const provider = inputs?.provider || 'static';
    if (provider === 'template') {
        return (
            <textarea value={params.brief || ''} onChange={(e) => setParam('brief', e.target.value)} disabled={disabled}
                placeholder="Paste the brief…" style={{ width: '100%', height: 220, padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12, fontFamily: 'monospace' }} />
        );
    }
    const fields = inputs?.params || [];
    return (
        <div>
            {fields.map((name) => {
                if (name === 'open_to_major_restructure') {
                    return <label key={name} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
                        <input type="checkbox" checked={!!params[name]} disabled={disabled} onChange={(e) => setParam(name, e.target.checked)} /> open to major restructure
                    </label>;
                }
                const isArea = name === 'preserve' || name === 'rewrite_goal';
                return (
                    <div key={name} style={{ marginBottom: 8 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>{name.replace(/_/g, ' ')}</label>
                        {isArea
                            ? <textarea value={params[name] || ''} disabled={disabled} onChange={(e) => setParam(name, e.target.value)} style={{ width: '100%', height: 60, padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }} />
                            : <input type={name === 'movie_id' ? 'number' : 'text'} value={params[name] || ''} disabled={disabled} onChange={(e) => setParam(name, e.target.value)} style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }} />}
                    </div>
                );
            })}
        </div>
    );
}

// ── Page ──
export default function StudioIndex({ blueprint, label }) {
    const [config, setConfig] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [status, setStatus] = useState('idle');
    const [round, setRound] = useState(0);
    const [agentOutputs, setAgentOutputs] = useState({});
    const [coordinator, setCoordinator] = useState(null);
    const [artifact, setArtifact] = useState(null);
    const [selected, setSelected] = useState({});
    const [producing, setProducing] = useState(false);
    const [params, setParams] = useState({});
    const [globalSteer, setGlobalSteer] = useState('');
    const [agentSteers, setAgentSteers] = useState({});
    const [events, setEvents] = useState([]);
    const clientRef = useRef(null);

    useEffect(() => {
        fetch(`/api/studio/${blueprint}/config`).then((r) => r.json()).then(setConfig).catch(() => {});
        return () => { if (clientRef.current) clientRef.current.end(true); };
    }, [blueprint]);

    const agentLabels = useMemo(() => {
        const m = {};
        (config?.agents || []).forEach((a) => { m[a.key] = a.label; });
        return m;
    }, [config]);

    const addEvent = useCallback((text) => setEvents((p) => [...p.slice(-80), { t: new Date().toLocaleTimeString(), text }]), []);

    const onMessage = useCallback((topic, message) => {
        let payload;
        try { payload = JSON.parse(message.toString()); } catch { return; }
        if (topic.endsWith('/status')) {
            const ev = payload.event;
            addEvent(`status: ${ev}${payload.agent ? ` [${payload.agent}]` : ''}`);
            if (ev === 'round_started') { setRound(payload.round); setStatus('running'); }
            else if (ev === 'waiting_for_human') { setStatus('waiting'); setProducing(false); }
            else if (ev === 'producing_json') { setStatus('producing_json'); setProducing(true); }
            else if (ev === 'json_produced') setProducing(false);
            else if (ev === 'json_production_failed') { setProducing(false); addEvent(`production failed: ${payload.error || ''}`); }
            else if (ev === 'session_accepted') setStatus('accepted');
            else if (ev === 'session_stopped') setStatus('stopped');
            else if (ev === 'coordinator_done') setRound(payload.round);
            else if (ev === 'ready') setStatus('running');
        } else if (topic.includes('/agent/')) {
            const m = topic.match(/\/agent\/([^/]+)\/output/);
            if (m) setAgentOutputs((p) => ({ ...p, [m[1]]: payload }));
        } else if (topic.includes('/coordinator/synthesis')) {
            setCoordinator(payload);
            const sel = {}; (payload.proposed_changes || []).forEach((c) => { sel[c.id] = true; });
            setSelected(sel);
        } else if (topic.includes('/working_output')) {
            if (payload.artifact) setArtifact(payload.artifact);
        }
    }, [addEvent]);

    const start = async () => {
        setStatus('running'); setAgentOutputs({}); setCoordinator(null); setArtifact(null); setSelected({}); setEvents([]);
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        try {
            const res = await fetch(`/api/studio/${blueprint}/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf },
                body: JSON.stringify({ params }),
            });
            const data = await res.json();
            if (!data.success) { setStatus('idle'); addEvent('failed to start'); return; }
            const sid = data.session_id;
            setSessionId(sid);
            const client = mqtt.connect(WS_URL);
            clientRef.current = client;
            client.on('connect', () => {
                client.subscribe(`studio/${sid}/#`, () => {
                    addEvent('connected; beginning');
                    client.publish(`studio/${sid}/control`, JSON.stringify({ action: 'begin' }), { qos: 1 });
                });
            });
            client.on('message', onMessage);
            client.on('error', (e) => addEvent(`mqtt error: ${e.message}`));
        } catch (e) { setStatus('idle'); addEvent(`http error: ${e.message}`); }
    };

    const control = (payload) => {
        if (clientRef.current && sessionId) clientRef.current.publish(`studio/${sessionId}/control`, JSON.stringify(payload), { qos: 1 });
        addEvent(`sent: ${payload.action}`);
    };

    const isWaiting = status === 'waiting';
    const active = ['running', 'waiting', 'producing_json'].includes(status);

    if (!config) return <div style={{ padding: 40, color: '#6b7280' }}>Loading {label || blueprint}…</div>;

    return (
        <div style={{ display: 'flex', height: '100vh', background: '#f5f5f5', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1f2937' }}>
            {/* Left */}
            <div style={{ width: '34%', minWidth: 340, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#083A54' }}>{config.label}</h1>
                    <StatusPill status={status} />
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px' }}>
                    {config.goal && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>{config.goal}</p>}
                    {!active && status !== 'accepted' && (
                        <>
                            <Intake inputs={config.inputs} params={params} setParam={(k, v) => setParams((p) => ({ ...p, [k]: v }))} disabled={active} />
                            <button onClick={start} style={{ width: '100%', marginTop: 10, padding: '9px 0', borderRadius: 8, border: 'none', background: '#083A54', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Start Session</button>
                        </>
                    )}
                    {active && (
                        <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Round <strong>{round}</strong></div>
                            <textarea value={globalSteer} onChange={(e) => setGlobalSteer(e.target.value)} placeholder="Steering note for next round…" style={{ width: '100%', height: 60, padding: 8, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }} />
                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                <button onClick={() => control({ action: 'next_round' })} disabled={!isWaiting} style={mainBtn(isWaiting, '#083A54')}>Next Round</button>
                                <button onClick={() => { if (globalSteer.trim()) { control({ action: 'next_round_steer', steer: globalSteer }); setGlobalSteer(''); } }} disabled={!isWaiting || !globalSteer.trim()} style={mainBtn(isWaiting && globalSteer.trim(), '#8b5cf6')}>+ Steer</button>
                            </div>
                            <button onClick={() => control({ action: 'accept' })} disabled={!isWaiting} style={{ ...mainBtn(isWaiting, '#10b981'), width: '100%', marginTop: 8 }}>Accept</button>
                            <button onClick={() => control({ action: 'stop' })} style={{ width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Stop</button>
                        </div>
                    )}
                    <div style={{ marginTop: 16, maxHeight: 160, overflowY: 'auto', background: '#f9fafb', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'monospace', color: '#6b7280' }}>
                        {events.map((e, i) => <div key={i}><span style={{ color: '#9ca3af' }}>{e.t}</span> {e.text}</div>)}
                    </div>
                </div>
            </div>

            {/* Right */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
                {status === 'idle'
                    ? <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>Fill the intake and start a session.</div>
                    : (
                        <>
                            {(config.agents || []).map((a) => (
                                <AgentCard key={a.key} meta={a} data={agentOutputs[a.key]} steer={agentSteers[a.key]}
                                    onSteer={(k, v) => setAgentSteers((p) => ({ ...p, [k]: v }))}
                                    onRerun={(k) => { control({ action: 'run_agent', target: k, steer: agentSteers[k] || null }); setAgentSteers((p) => ({ ...p, [k]: '' })); }}
                                    running={status === 'running'} />
                            ))}
                            <QuestionInbox agentOutputs={agentOutputs} agentLabels={agentLabels} />
                            <CoordinatorPanel data={coordinator} agentLabels={agentLabels} selected={selected}
                                onToggle={(id) => setSelected((p) => ({ ...p, [id]: !p[id] }))}
                                onAll={() => { const s = {}; (coordinator?.proposed_changes || []).forEach((c) => { s[c.id] = true; }); setSelected(s); }}
                                onNone={() => setSelected({})}
                                onProduce={() => { const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k); if (ids.length) { setProducing(true); control({ action: 'produce_json', selected_changes: ids }); } }}
                                producing={producing} />

                            {/* Artifact via the blueprint's display schema */}
                            <div style={{ border: '1px solid #d1d5db', borderLeft: '4px solid #059669', borderRadius: 8, background: '#fff', padding: '12px 14px' }}>
                                <div style={{ fontWeight: 700, color: '#059669', fontSize: 14, marginBottom: 10 }}>Working Artifact {round > 0 && <span style={{ fontSize: 11, color: '#9ca3af' }}>round {round}</span>}</div>
                                {artifact && config.display_schema
                                    ? <SchemaNode node={config.display_schema} data={artifact} />
                                    : <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 13 }}>Artifact appears here once produced.</p>}
                            </div>
                        </>
                    )}
            </div>
        </div>
    );
}

function mainBtn(enabled, color) {
    return { flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: enabled ? color : '#d1d5db', color: '#fff', fontSize: 12, fontWeight: 600, cursor: enabled ? 'pointer' : 'not-allowed' };
}
